import React, { useState } from 'react';
import {
    IconTextButton,
    ITreeSelectDataSource,
    notification,
    AdvanceTable,
    IBasicTableProps,
    IAdvanceTableToolbarProps,
} from 'ui-lib';
import uploadFileCss from './new-file-transfer.css';
import { downloadFileFromUrl, getErrorMessage } from 'common-utils';
import { TableUtils } from 'pages/private/components/table-utils';
import { confirmAction } from 'pages/private/admin/utils';
import { CreateTransferFile } from './create-transfer-file';
import { UploadFileDto, UploadFileType } from 'dto/upload-file.dto';
import { UploadFile } from './upload-file';
import { useUploadFilesStore } from 'store';
import { set } from 'lodash';

interface IUploadFilesTabProps {
    selectedClientAccountId: number;
    deviceGroupsDataSource: ITreeSelectDataSource[];
    deviceGroupLoading: boolean;
    lockOrganisationSelect: (boolean) => void;
}

export enum UPLOAD_FILE_STATE {
    LIST = 'List',
    UPLOAD = 'Upload',
    CREATE_TRANSFER_FILE = 'Create_Transfer_File',
}

export const UploadFilesTab = (props: IUploadFilesTabProps) => {
    const uploadFilesStore = useUploadFilesStore();
    const [uploadedFilesSource, setUploadFilesSource] = React.useState<UploadFileDto[]>([]);
    const [state, setState] = useState<UPLOAD_FILE_STATE>(UPLOAD_FILE_STATE.LIST);
    const [reloading, setReloading] = React.useState<boolean>(false);
    const [selectedUploadFile, setSelectedUploadFile] = React.useState<UploadFileDto>(null);

    const OnDeleteButtonClick = async (uploadFile: UploadFileDto) => {
        confirmAction(
            undefined,
            'Delete Uploaded File',
            `Are you sure you want to delete the uploaded file ${uploadFile.fileName}?`,
            async () => {
                try {
                    await uploadFilesStore.deleteUploadedFile(props.selectedClientAccountId, uploadFile.uuid);
                    setUploadFilesSource((prev) => prev.filter((item) => item.uuid !== uploadFile.uuid));
                    notification.success({
                        message: 'Uploaded File Successfully Deleted',
                        description: `Uploaded file ${uploadFile.fileName} has been deleted.`,
                    });

                } catch (error) {
                    notification.error({
                        message: 'Delete uploaded file error',
                        description: getErrorMessage(error),
                    });
                }
            });
    }

    const ActionComponent = (props: { clientAccountId: number, record: UploadFileDto }): JSX.Element => {
        const uploadFile: UploadFileDto = props.record;
        const access = uploadFile.type !== UploadFileType.SCR;
        return (
            <div className={uploadFileCss.actionButtons}>
                <IconTextButton
                    className={uploadFileCss.linkButton}
                    key='transfer'
                    type='link'
                    label='Transfer'
                    onClick={() => {
                        setState(UPLOAD_FILE_STATE.CREATE_TRANSFER_FILE);
                        setSelectedUploadFile(uploadFile);
                    }}
                />
                <IconTextButton
                    className={uploadFileCss.linkButton}
                    key='download'
                    type='link'
                    label='Download'
                    disabled={!access}
                    onClick={() => {
                        downloadFileFromUrl(uploadFile.awsArn, uploadFile.fileName);
                    }}
                />
                <IconTextButton
                    className={uploadFileCss.linkButton}
                    key='uploadFileDelete'
                    type='link'
                    label='Delete'
                    onClick={() => OnDeleteButtonClick(uploadFile)}
                    disabled={!access}
                />
            </div>
        );
    };

    const actionsColumn = TableUtils.createExtraColumn({
        key: 'actions',
        title: 'Actions',
        render: (text: string, record: UploadFileDto) => <ActionComponent clientAccountId={props.selectedClientAccountId} record={record} />,
    });

    const uploadFilesTable: IBasicTableProps = {
        dataSource: uploadedFilesSource,
        columns: [
            TableUtils.createBasicFilterColumn({ title: 'File Name', key: 'fileName' }),
            TableUtils.createBasicEnumFilterColumn({
                title: 'Type',
                key: 'type',
                filters: Object.keys(UploadFileType).map(
                    (type) => ({ text: UploadFileType[type], value: type })
                ),
            }),
            TableUtils.createBasicFilterColumn({ title: 'Description', key: 'description' }),
            TableUtils.createBasicFilterTimeColumn({ key: 'createdAt', title: 'Created At' }),
            actionsColumn,
        ],
    };

    const resetData = () => {
        setUploadFilesSource([]);
        setSelectedUploadFile(null);
    }

    const onReloadData = async () => {
        setReloading(true);
        try {
            resetData();
            if (!props.selectedClientAccountId) {
                setReloading(false);
                return;
            }

            const uploadFiles = await uploadFilesStore.getUploadedFiles(props.selectedClientAccountId);
            setUploadFilesSource(uploadFiles);

        } catch (error) {
            notification.error({
                message: 'Error',
                description: `Failed to load upload files: ${getErrorMessage(error)}`,
            });
        }
        setReloading(false);
    };

    const onAdd = () => {
        if (props.selectedClientAccountId == null) {
            return notification.warning({
                message: 'Warning',
                description: 'Please select an organisation before uploading files.',
            });
        }
        setState(UPLOAD_FILE_STATE.UPLOAD);
    };

    const onCancel = async () => {
        setState(UPLOAD_FILE_STATE.LIST);
        await onReloadData();
    }

    React.useEffect(() => {
        (async () => {
            await onReloadData();
        })();
    }, [props.selectedClientAccountId]);

    React.useEffect(() => {
        if (state === UPLOAD_FILE_STATE.LIST) {
            props.lockOrganisationSelect(false);
        }
        else {
            props.lockOrganisationSelect(true);
        }
    }, [state]);

    const uploadFilesTableToolbar: IAdvanceTableToolbarProps = {
        onAdd: onAdd,
        onReload: onReloadData,
    };

    return (
        <React.Fragment>
            {state === UPLOAD_FILE_STATE.LIST && (
                <AdvanceTable
                    toolbar={uploadFilesTableToolbar}
                    title='Uploaded Files'
                    table={uploadFilesTable}
                    loadingState={reloading}
                />
            )}
            {state === UPLOAD_FILE_STATE.UPLOAD && (
                <UploadFile
                    selectedClientAccountId={props.selectedClientAccountId}
                    onCancel={onCancel}
                />
            )}
            {state === UPLOAD_FILE_STATE.CREATE_TRANSFER_FILE && (
                <CreateTransferFile
                    deviceGroupsDataSource={props.deviceGroupsDataSource}
                    selectedClientAccountId={props.selectedClientAccountId}
                    deviceGroupLoading={props.deviceGroupLoading}
                    uploadFile={selectedUploadFile}
                    onCancel={onCancel}
                />
            )}
        </React.Fragment>
    );
};